<template>
  <NuxtLayout name="default-full-width">
    <div
      id="error"
      class="flex flex-col items-center justify-center px-4 mx-auto"
    >
      <template v-if="error?.statusCode === STATUS_RESOURCE_NOT_FOUND">
        <div class="flex flex-col items-center mt-10 lg:mt-24">
          <NoResults class="mb-10 lg:mb-14" />
          <div v-if="isDepartmentBlockExists">
            <RenderContent :content="departmentBlockContent" />
          </div>
          <div v-if="isTrustedBrandsExists">
            <RenderContent :content="trustedBrandsContent" />
          </div>
        </div>
      </template>
      <template v-else>
        <ImgView
          class="image"
          src="/error/error.svg"
          alt="leaves"
        />
        <SfHeading
          :title="t('error-occured')"
          :level="2"
          :description="t('go-back-or-try-again')"
          class="heading sf-heading--no-underline"
        />
        <div class="actions">
          <div class="py-10">
            <CustomButton
              type="tertiary"
              :text="t('go-back-to-home-page')"
              @click="router.push('/')"
            />
          </div>
          <CustomButton
            type="link"
            :text="t('Back')"
            @click="router.go(-1)"
          />
        </div>
      </template>
    </div>
  </NuxtLayout>
</template>

<script setup lang="ts">
import SfHeading from '~/components/atoms/SfHeading/SfHeading.vue';
import CustomButton from '~/components/atoms/Buttons/Button/CustomButton.vue';
import ImgView from '~/components/atoms/ImgView/ImgView.vue';

import { COMPONENTS } from '~~/constants/cms';
import { STATUS_RESOURCE_NOT_FOUND } from '~~/constants/http';

type Props = {
  error?: {
    statusCode?: number;
    message?: string;
  };
};

const props = defineProps<Props>();

const { t } = useI18n();
const router = useRouter();

const {
  loadComponent: departmentBlockSearch,
  internalCmsContent: departmentBlockContent,
  canShowContent: isDepartmentBlockExists,
} = useInternalCms(COMPONENTS.SHOP_BY_DEPARTMENT.SLUG);

const {
  loadComponent: trustedBrandsSearch,
  internalCmsContent: trustedBrandsContent,
  canShowContent: isTrustedBrandsExists,
} = useInternalCms(COMPONENTS.TRUSTED_BRANDS.SLUG);

const load404Cms = async () => {

  await Promise.all([departmentBlockSearch(), trustedBrandsSearch()]);

};

if (import.meta.server && props.error?.statusCode === STATUS_RESOURCE_NOT_FOUND) {

  await load404Cms();

}

onMounted(() => {

  if (props.error?.statusCode === STATUS_RESOURCE_NOT_FOUND) {

    void load404Cms();

  }

});
</script>

<style lang="scss" scoped>
@use "~/assets/scss/helpers/_breakpoints" as breakpoints;
.image {
  width: 14.375rem;
  padding: var(--spacer-xl) 0;

  @include breakpoints.for-desktop {
    width: 25.75rem;
  }
}

.heading {
  --heading-title-margin: 0 0 var(--spacer-sm);
  --heading-title-color: var(--c-primary);
  --heading-title-font-weight: var(--font-weight--semibold);
  --heading-description-color: var(--c-text-muted);
  --heading-description-font-size: var(--font-size--base);
  --heading-description-margin: 0 var(--spacer-base);
  --heading-description-font-family: var(--font-family--primary);

  @include breakpoints.for-desktop {
    --heading-description-margin: 0;
  }
}

.actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-direction: column;
  width: 100%;
  margin: var(--spacer-xl) 0 0 0;

  &__button {
    --button-width: 100%;

    &:last-child {
      margin: var(--spacer-sm) 0;
    }

    &:first-child:hover {
      color: var(--c-white);
    }
  }

  @include breakpoints.for-desktop {
    margin: var(--spacer-lg) 0 0 0;

    &__button {
      --button-width: 25rem;
    }
  }
}
</style>
